import type { ConstantsTeamResponse } from "@gosaas/types";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import {
	apiClient,
	requestWithHandling,
	TypedClientApiError,
} from "@/utils/api";

type TeamContextType = {
	teams: ConstantsTeamResponse[];
	loading: boolean;
	error: string | null;
	teamID: string | null;
	setTeam: (teamId: string) => void;
	refresh: () => Promise<void>;
};

const TeamContext = createContext<TeamContextType | null>(null);

export function resolveTeamIdAfterTeamsLoad(
	currentTeamId: string | null,
	teams: ConstantsTeamResponse[],
): string | null {
	let teamIdToSet = currentTeamId;
	if (!teamIdToSet && teams.length > 0) {
		const savedTeamID = localStorage.getItem("teamID");
		teamIdToSet =
			savedTeamID && teams.find((t) => t.id === savedTeamID)
				? savedTeamID
				: teams[0].id;
		localStorage.setItem("teamID", teamIdToSet);
	} else if (teamIdToSet && !teams.find((t) => t.id === teamIdToSet)) {
		teamIdToSet = teams.length > 0 ? teams[0].id : null;
		if (teamIdToSet) {
			localStorage.setItem("teamID", teamIdToSet);
		} else {
			localStorage.removeItem("teamID");
		}
	}

	return teamIdToSet;
}

export const TeamProvider = ({ children }: { children: React.ReactNode }) => {
	const [state, setState] = useState<{
		teams: ConstantsTeamResponse[];
		isInitialLoading: boolean;
		error: string | null;
		teamID: string | null;
	}>({
		teams: [],
		isInitialLoading: true,
		error: null,
		teamID: localStorage.getItem("teamID") ?? null,
	});

	const setTeam = (teamId: string) => {
		localStorage.setItem("teamID", teamId);
		setState((prev) => ({
			...prev,
			teamID: teamId,
		}));
	};

	const refresh = useCallback(async () => {
		setState((prev) => {
			if (!prev.isInitialLoading) {
				return { ...prev, error: null };
			}
			return prev;
		});

		try {
			const client = await requestWithHandling<ConstantsTeamResponse[]>(() =>
				apiClient.api.userTeamsList(),
			);
			const teams = client.data ?? [];

			setState((prev) => {
				const teamIdToSet = resolveTeamIdAfterTeamsLoad(prev.teamID, teams);
				return {
					...prev,
					teams,
					isInitialLoading: false,
					error: null,
					teamID: teamIdToSet,
				};
			});
		} catch (error) {
			// Only log error message in dev mode to prevent sensitive data exposure
			if (import.meta.env.DEV) {
				const errorMessage =
					error instanceof TypedClientApiError
						? error.message
						: "Failed to load teams";
				console.error("Failed to fetch teams:", errorMessage);
			}
			const errorMessage =
				error instanceof TypedClientApiError
					? error.message
					: "Failed to load teams. Please try again.";

			setState((prev) => ({
				...prev,
				isInitialLoading: false,
				error: errorMessage,
			}));
		}
	}, []);

	// biome-ignore lint/correctness/useExhaustiveDependencies: Need to run only on mount
	useEffect(() => {
		refresh();
	}, []);

	return (
		<TeamContext.Provider
			value={{
				teams: state.teams,
				loading: state.isInitialLoading,
				error: state.error,
				teamID: state.teamID,
				setTeam,
				refresh,
			}}
		>
			{children}
		</TeamContext.Provider>
	);
};

export const useTeam = () => {
	const context = useContext(TeamContext);
	if (!context) {
		throw new Error("useTeam must be used within a TeamProvider");
	}
	return context;
};
